import { isSupportedCountry, parsePhoneNumberFromString } from "libphonenumber-js/min";

const EG_NATIONAL_DIGITS = 10; // libphonenumber accepts any EG number longer than 8 digits, we only accept full mobiles
const DEFAULT_COUNTRY = 'EG';

// forms hold the number in whatever shape the input produced ("201012345678", "+20 100 123 4567", ...)
// parsePhoneNumberFromString returns undefined on unparsable input, it never throws
const parsePhone = (value) => parsePhoneNumberFromString(value.startsWith('+') ? value : `+${value}`);

export const checkPhoneValidation = (initial) => {
  if (typeof initial !== 'string' || initial.length < 8) return false;
  // a lettered value is read as an Egyptian number with the noise stripped out
  const parsedNumber = parsePhone(/[a-z]/i.test(initial) ? `+20${initial.replace(/\D/g, '')}` : initial);
  if (!parsedNumber?.isValid()) return false;
  if (parsedNumber.country === 'EG') {
    return parsedNumber.nationalNumber.length === EG_NATIONAL_DIGITS;
  }
  return true;
};

export const getPhoneCountryCode = (initial, fallback) => {
  if (typeof initial !== 'string' || initial.length < 8) return fallback?.toLocaleLowerCase();
  const parsedNumber = parsePhone(initial);
  if (!parsedNumber) return fallback?.toLocaleLowerCase(); // unparsable, keep the caller's country
  return parsedNumber.country?.toLocaleLowerCase();
};

/*
 * the input reads its value as a full international number, so a phone stored in national
 * shape ("01000000000") is drawn with a dial code that does not match the selected flag
 * ("+01 000 000 000" while the flag says +20) and then rejects every keystroke => rewrite
 * such a value into the shape the input produces.
 *
 * Only a value that parses as a COMPLETE national number is rewritten: prefixing a dial code
 * onto anything else would corrupt half-typed numbers and bare dial codes (picking Saudi
 * hands us "966", which must stay "966" and never become "20966").
 */
export const normalizeStoredPhone = (initial, fallback) => {
  const original = String(initial ?? '');
  const digits = original.replace(/\D/g, '');
  if (!digits) return original;

  if (parsePhone(digits)?.isValid()) return digits; // already an international number

  const country = String(fallback ?? '').toLocaleUpperCase();
  const nationalNumber = parsePhoneNumberFromString(digits, isSupportedCountry(country) ? country : DEFAULT_COUNTRY);

  return nationalNumber?.isValid()
    ? `${nationalNumber.countryCallingCode}${nationalNumber.nationalNumber}`
    : original; // unparsable, leave it exactly as it came
};

export const formatPhoneForView = (initial) => {
  const value = String(initial ?? '').trim();
  if (!value) return '';

  const parsedNumber = parsePhone(value);
  if (parsedNumber?.countryCallingCode && parsedNumber?.nationalNumber) {
    return `+${parsedNumber.countryCallingCode}-${parsedNumber.nationalNumber}`;
  }

  const digits = value.replace(/\D/g, '');
  if (!digits) return value;

  if (digits.length > EG_NATIONAL_DIGITS) {
    return `+${digits.slice(0, -EG_NATIONAL_DIGITS)}-${digits.slice(-EG_NATIONAL_DIGITS)}`;
  }

  return value.startsWith('+') ? value : `+${value}`;
};
